import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  VStack,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { useUpdateSponsor } from 'queries/sponsors.query'
import Dropzone from '../../settings/components/Dropzone'
import { getStorageImageUrl } from 'utils/imageUrl'

// Validation schema for the form fields
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Sponsor name is required.'),
  website: Yup.string()
    .url('Website must be a valid URL.')
    .required('Website is required.'),
})

export const EditSponsorModal = ({ isOpen, onClose, sponsor }) => {
  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')

  const toast = useToast()
  const { mutate, isPending } = useUpdateSponsor()

  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)

  useEffect(() => {
    if (sponsor && sponsor.logo) {
      setLogoPreview(getStorageImageUrl(sponsor.logo))
    } else {
      setLogoPreview(null)
    }
    // Clean up temporary object URL when the modal closes or component unmounts
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview)
      }
    }
  }, [sponsor, isOpen])

  const handleFileChange = file => {
    setLogoFile(file)
    if (file) {
      // Create a temporary URL for the new file preview
      setLogoPreview(URL.createObjectURL(file))
    } else {
      // If no file is selected, reset preview
      setLogoPreview(null)
    }
  }

  const handleSubmit = (values, { setSubmitting }) => {
    const formData = new FormData()
    formData.append('name', values.name)
    formData.append('website', values.website)

    // Only append the logo file if a new one was selected
    if (logoFile) {
      formData.append('logo', logoFile)
    }

    mutate(
      { id: sponsor.id, updatedData: formData },
      {
        onSuccess: () => {
          toast({
            title: 'Sponsor updated.',
            description: 'The sponsor has been updated successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
          onClose()
        },
        onError: error => {
          const backendMessage = error.response?.data?.message || error.response?.data?.error || error?.message || 'An error occurred while updating the sponsor.'
          toast({
            title: 'Update failed.',
            description: backendMessage,
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'top',
          })
        },
        onSettled: () => {
          setSubmitting(false)
        },
      },
    )
  }

  // Early return if sponsor data is not yet available
  if (!sponsor) {
    return null
  }

  const initialValues = {
    name: sponsor.name || '',
    website: sponsor.website || '',
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Sponsor</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align='center'>
                  <FormControl
                    id='name'
                    isInvalid={errors.name && touched.name}
                    isRequired
                  >
                    <FormLabel>Sponsor Name</FormLabel>
                    <Field
                      as={Input}
                      name='name'
                      color={inputColor}
                      backgroundColor={backgroundColor}
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                  </FormControl>
                  <FormControl
                    id='website'
                    isInvalid={errors.website && touched.website}
                    isRequired
                  >
                    <FormLabel>Website URL</FormLabel>
                    <Field
                      as={Input}
                      name='website'
                      color={inputColor}
                      backgroundColor={backgroundColor}
                    />
                    <FormErrorMessage>{errors.website}</FormErrorMessage>
                  </FormControl>
                  <FormControl id='logo'>
                    <FormLabel>Logo</FormLabel>
                    <Dropzone
                      title='Upload New Logo'
                      onFileChange={handleFileChange}
                      preview={logoPreview}
                    />
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme='blue' type='submit' isLoading={isPending}>
                  Save
                </Button>
                <Button colorScheme='gray' ml={3} onClick={onClose}>
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}
